import React, { useEffect, useMemo, useState } from 'react';
import { ActionButton, Buttons, CancelButton, Drawer, Icon, SingleSelect, Table, Tooltip } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import Fuse from 'fuse.js';
import { colors } from '~/styles';
import _ from 'lodash';

export default function ExpenseCategoryMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const { xeroConnectionId } = useParams();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);

  const [{ categories, accounts, isReady }, setState] = useState({ categories: null, isReady: false });

  useEffect(() => {
    (async () => {
      const { data: categories } = await api.www
        .workspaces(workspace.id)
        .xero(xeroConnectionId)
        .getExpenseCategoryMappings();
      const { data: accounts } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getExpenseAccounts();

      if (!isMounted.current) return;
      setState({ categories, accounts, isReady: true });
    })();
  }, [api, workspace.id, isMounted, xeroConnectionId]);

  const updateCategoryMapping = ({ expenseCategoryId, xeroAccountId, xeroAccountCode }) => {
    setDirty(true);

    setState((state) => ({
      ...state,
      categories: state.categories.map((c) =>
        c.id === expenseCategoryId ? { ...c, xeroAccountId, xeroAccountCode } : c,
      ),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Expense Category Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();

          try {
            const mappings = categories.map((category) => ({
              expenseCategoryId: category.id,
              xeroAccountId: category.xeroAccountId,
              xeroAccountCode: category.xeroAccountCode,
            }));
            await api.www.workspaces(workspace.id).xero(xeroConnectionId).updateExpenseCategoryMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Expense Category</Table.Column>

                <Table.Column>
                  <strong>Xero Expense Account</strong>
                </Table.Column>
              </Table.BoxHeader>

              <Table.Body>
                {categories.map((category) => {
                  const handleAccountChange = (event) => {
                    const account = accounts.find((a) => a.id === event.target.value);
                    updateCategoryMapping({
                      expenseCategoryId: category.id,
                      xeroAccountId: event.target.value || null,
                      xeroAccountCode: account?.code || null,
                    });
                  };

                  return (
                    <Table.BoxRow key={category.id}>
                      <Table.Cell>{category.name}</Table.Cell>
                      <Table.Cell>
                        <AccountSelect
                          accounts={accounts}
                          value={category.xeroAccountId}
                          onChange={handleAccountChange}
                        />
                      </Table.Cell>
                    </Table.BoxRow>
                  );
                })}
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}

function AccountSelect({ accounts, value, ...props }) {
  const [filterValue, setFilterValue] = useState('');

  const options = useMemo(() => {
    if (!filterValue) return accounts;
    const fuse = new Fuse(accounts, { keys: ['name', 'code'], threshold: 0.1 });
    return fuse.search(filterValue).map((result) => result.item);
  }, [accounts, filterValue]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const account = _.find(accounts, { id: value });
    if (!account)
      return {
        id: value,
        name: (
          <Tooltip message="Account not found in Xero.">
            <Icon icon="warning" color={colors.warning} spaceRight />
            Account ID: {value}
          </Tooltip>
        ),
      };
    return account;
  }, [accounts, value]);

  return (
    <SingleSelect
      showEmptyOption
      showFilter
      value={value}
      valueRenderer={selectedOption?.name ?? ''}
      onFilterChange={(event) => setFilterValue(event.target.value)}
      {...props}>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </SingleSelect>
  );
}
